/** Transformation des fichiers renvoyés par l'API en téléchargements locaux. */

import type { GeneratedFile, SupplierDownload } from '@/api/types'

/**
 * Les fichiers arrivent encodés dans la réponse de conversion : rien n'attend
 * sur le serveur, et le téléchargement ne demande pas un second aller-retour.
 */
export function toBlobUrl(file: GeneratedFile): string {
  return URL.createObjectURL(new Blob([decode(file.content)], { type: file.media_type }))
}

/**
 * Le catalogue récupéré chez le fournisseur redevient un vrai fichier, qui
 * repart ensuite vers l'analyse comme s'il avait été déposé à la main : un
 * seul chemin de traitement, qu'on ait cliqué ou glissé-déposé.
 */
export function toFile(download: SupplierDownload): File {
  return new File([decode(download.content)], download.name, { type: download.media_type })
}

function decode(base64: string) {
  const binary = atob(base64)
  // Le tampon est alloué explicitement : sans cela le type inféré autorise un
  // `SharedArrayBuffer`, que `Blob` et `File` n'acceptent pas.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export function downloadJson(data: unknown, filename: string): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
  triggerDownload(url, filename)
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

export function triggerDownload(url: string, filename: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} o`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} Ko`
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return `${date.toLocaleDateString('fr-FR')} à ${date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}
